/**
 * ExecutiveReportDialog — shared modal for clients & admins to generate
 * a 1-page executive PDF + heatmap snapshots.
 *
 * Props:
 *   open, onClose
 *   floorPlans: [{ id, name }]    // already scoped (client: own; admin: chosen client)
 *   clientId?: string             // required when caller is admin
 *   onGenerated?: (report) => {}  // fires after successful POST
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileDown, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const MONTH_OPTIONS = [
    { value: 3, label: "Últimos 3 meses" },
    { value: 6, label: "Últimos 6 meses" },
    { value: 12, label: "Últimos 12 meses" },
    { value: 24, label: "Últimos 24 meses" },
];

export default function ExecutiveReportDialog({ open, onClose, floorPlans = [], clientId, onGenerated }) {
    const [months, setMonths] = useState(3);
    const [selected, setSelected] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            // default: select all plans
            setSelected(floorPlans.map((p) => p.id));
            setMonths(3);
        }
    }, [open, floorPlans]);

    const toggle = (id) => {
        setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const submit = async () => {
        setBusy(true);
        try {
            const body = { months, floor_plan_ids: selected };
            if (clientId) body.client_id = clientId;
            const { data } = await api.post("/executive-reports", body);
            toast.success("Informe generado correctamente");
            onGenerated?.(data);
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo generar el informe");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="executive-report-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        Generar Informe de Actividad MIP
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <Label className="text-xs">Periodo de análisis</Label>
                        <Select value={String(months)} onValueChange={(v) => setMonths(parseInt(v, 10))}>
                            <SelectTrigger className="mt-1" data-testid="exec-report-months">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {MONTH_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="text-xs">Planos para incluir mapa de calor</Label>
                        {floorPlans.length === 0 ? (
                            <div className="text-xs text-muted-foreground py-2">
                                No hay planos disponibles. El informe se generará sin mapas de calor.
                            </div>
                        ) : (
                            <div className="space-y-1.5 mt-2 max-h-44 overflow-y-auto border rounded-md p-2">
                                {floorPlans.map((p) => (
                                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1.5 py-1 rounded">
                                        <Checkbox
                                            checked={selected.includes(p.id)}
                                            onCheckedChange={() => toggle(p.id)}
                                            data-testid={`exec-plan-${p.id}`}
                                        />
                                        <span className="truncate">{p.name}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                        <p className="text-[10px] text-muted-foreground mt-1">
                            Cada plano añade una página adicional con el mapa de calor.
                        </p>
                    </div>

                    <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-900 flex gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>
                            El informe incluirá comparativas vs el periodo anterior y un resumen ejecutivo apto para enviar a tu cliente.
                            <br />
                            <b>Nota:</b> sólo se conservan los <b>3 informes más recientes</b> por cliente — los anteriores se eliminan automáticamente.
                        </span>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy} data-testid="exec-report-cancel">
                        Cancelar
                    </Button>
                    <Button onClick={submit} disabled={busy} data-testid="exec-report-submit">
                        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                        Generar informe
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
